// ── Owner ⇄ contact fuzzy name matching ──────────────────────────────────────
// Shared by Property import (owner name → existing contact) and Contact import
// (new contact → existing listing property's owner). Matches first & last name
// only — middle names are ignored — with a 1-character typo tolerance.

export function normToken(s) {
  return (s || '').toLowerCase().normalize('NFKD').replace(/[^a-z\s]/g, '').replace(/\s+/g, ' ').trim();
}

// First and last name tokens of a full name, dropping any middle names.
export function firstLast(fullName) {
  const toks = normToken(fullName).split(' ').filter(Boolean);
  if (!toks.length) return null;
  return { first: toks[0], last: toks[toks.length - 1] };
}

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return dp[m][n];
}

// Equal, or within a 1-character edit (typo/spelling tolerance).
export function fuzzyEq(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  return levenshtein(a, b) <= 1;
}

// First/last tokens of a contact record (its firstName / lastName fields).
function contactFirstLast(c) {
  return {
    first: (normToken(c && c.firstName).split(' ')[0]) || '',
    last:  (normToken(c && c.lastName).split(' ').pop()) || '',
  };
}

// Does a free-text owner name fuzzily match a single contact record?
export function ownerMatchesContact(ownerName, contact) {
  const fl = firstLast(ownerName);
  if (!fl || !contact) return false;
  const cl = contactFirstLast(contact);
  return fuzzyEq(cl.first, fl.first) && fuzzyEq(cl.last, fl.last);
}

// Find a contact whose first & last name fuzzily match an owner name.
export function matchContact(ownerName, contacts) {
  if (!firstLast(ownerName)) return null;
  return (contacts || []).find(c => ownerMatchesContact(ownerName, c)) || null;
}
